import React, { useEffect, useMemo } from "react";
import { Component, ComponentType, Viewport } from "@/types";
import { BasicTable, AdvancedTable, DashboardTable } from "@/components/tables";
import {
  applyTableColumnVisibilityForBreakpoint,
  getBreakpointFromTableViewport,
} from "@/components/tables/table-responsive-columns";

export interface TableRendererProps {
  component: Component;
  viewport?: Viewport;
}

export const TableRenderer: React.FC<TableRendererProps> = React.memo(
  ({ component, viewport }) => {
    const { properties = {}, type } = component;
    const mergedComponent = useMemo(() => {
      const breakpoint = getBreakpointFromTableViewport(viewport);
      if (!breakpoint) return component;

      const breakpointProperties =
        component.responsivePosition?.breakpoints?.[breakpoint]?.properties;
      const baseProperties = breakpointProperties
        ? {
            ...properties,
            ...breakpointProperties,
          }
        : properties;
      const mergedProperties = applyTableColumnVisibilityForBreakpoint(
        baseProperties,
        breakpoint,
      );

      if (mergedProperties === properties) return component;

      return {
        ...component,
        properties: mergedProperties,
      };
    }, [component, properties, viewport]);

    const isDashboardTable =
      properties?.dashboardMode === true ||
      (Array.isArray(properties?.features) &&
        properties.features.includes("kpi") &&
        properties.features.includes("realtime"));
    const rendererKind =
      type === ComponentType.ADVANCED_TABLE || properties?.advanced === true
        ? "advanced"
        : isDashboardTable
          ? "dashboard"
          : "basic";

    useEffect(() => {
      if (!import.meta.env.DEV) return;

      console.debug("[TableRenderer] renderer selected", {
        componentId: component.id,
        componentType: type,
        rendererKind,
        advancedProperty: properties?.advanced === true,
        dashboardMode: properties?.dashboardMode === true,
        dataAdditionsEnabled: properties?.dataAdditionsEnabled === true,
        columnCount: Array.isArray(properties?.columns) ? properties.columns.length : 0,
        staticRowCount: Array.isArray(properties?.data) ? properties.data.length : 0,
      });
    }, [
      component.id,
      type,
      rendererKind,
      properties?.advanced,
      properties?.columns,
      properties?.dashboardMode,
      properties?.data,
      properties?.dataAdditionsEnabled,
    ]);

    if (type === ComponentType.ADVANCED_TABLE || properties?.advanced === true) {
      return (
        <AdvancedTable
          component={mergedComponent}
          viewport={viewport}
          mobileViewport={viewport === Viewport.MOBILE}
        />
      );
    }

    if (isDashboardTable) {
      return (
        <DashboardTable
          component={mergedComponent}
          mobileViewport={viewport === Viewport.MOBILE}
        />
      );
    }

    return <BasicTable component={mergedComponent} viewport={viewport} />;
  },
);

TableRenderer.displayName = "TableRenderer";
